import { Component, ElementRef, Injector, Input, OnInit, ViewChild } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AllergenEnum, DishDto, LikesDto, TypologyDto } from '@timechef/core/api';
import { I18nService } from '@timechef/core/i18n/i18n.service';
import { AccountRepository } from '@timechef/features/account/state';
import { BehaviorSubject } from 'rxjs';
import { RestaurantsRepository, RestaurantsService } from '../../../state';

@UntilDestroy()
@Component({
  selector: 'app-dish-details',
  templateUrl: './dish-details.component.html',
  styleUrls: ['./dish-details.component.scss'],
  standalone: false,
})
export class DishDetailsComponent implements OnInit {
  @Input() dish!: DishDto;
  @Input() restaurantId!: number;
  @Input() menuDate!: Date;
  @Input() showQuantityPicker!: Date;
  sliderOptions = { initialSlide: 0 };
  userAllergens: AllergenEnum[] = this.accountRepo.getAllergens();

  @ViewChild('title') titleEl?: ElementRef;

  dishLikes?: LikesDto;

  showDataInHeader$ = new BehaviorSubject(false);
  ingredients = '';
  restaurantsService: RestaurantsService;

  // to rework
  addButtonDisabled = false;
  removeButtonShown = true;

  constructor(
    private accountRepo: AccountRepository,
    private alertCtrl: AlertController,
    private modalCtrl: ModalController,
    private alertController: AlertController,
    private i18n: I18nService,
    private restaurantsRepo: RestaurantsRepository,
    injector: Injector,
  ) {
    this.restaurantsService = injector.get(RestaurantsService);
  }

  ngOnInit(): void {
    if (!this.dish?.mainImageUrl) {
      setTimeout(() => {
        this.showDataInHeader$.next(true);
      }, 0);
    }

    this.ingredients = this.dish.ingredients.join(' · ');

    this.restaurantsRepo
      .dishLikes$(this.restaurantId!, this.dish!.id)
      .pipe(untilDestroyed(this))
      .subscribe((x) => {
        this.dishLikes = x;
      });
  }

  async dismissModal() {
    await this.modalCtrl.dismiss();
  }

  onScroll(ev: any) {
    const y = ev.detail.scrollTop;
    this.showDataInHeader$.next(y >= (this.dish.imageUrls.length! > 1 ? CARROUSEL_IMG_HEIGHT : SINGLE_IMG_HEIGHT));
  }

  async onMarkersClick(markers: Array<TypologyDto>) {
    const title = this.i18n.translate('DISH_DETAILS_COMPONENT.MARKERS_TITLE', { count: markers.length });
    const subtitle = this.i18n.translate('DISH_DETAILS_COMPONENT.MARKERS_SUBTITLE');
    await this.restaurantsService.openTypologiesModal(title, subtitle, markers);
  }

  async onLabelsClick(labels: Array<TypologyDto>) {
    const title = this.i18n.translate('DISH_DETAILS_COMPONENT.LABELS_TITLE', { count: labels.length });
    const subtitle = this.i18n.translate('DISH_DETAILS_COMPONENT.LABELS_SUBTITLE');
    await this.restaurantsService.openTypologiesModal(title, subtitle, labels);
  }

  async onPartnersClick() {
    // TODO when partners will be managed
  }

  async onNutriscoreClick() {
    this.restaurantsService.openNutriscoreModal();
  }

  async onCarbonscoreClick() {
    this.restaurantsService.openCarbonscoreModal();
  }

  async onGreenscoreClick() {
    this.restaurantsService.openGreenscoreModal();
  }

  async onAllergensClick() {
    this.restaurantsService.openAllergensModal();
  }

  async onLikeClick(isLiked: boolean, canBeNotified: boolean) {
    let isNotificationEnabled = false;
    if (isLiked && canBeNotified) {
      const alert = await this.alertCtrl.create({
        message: this.i18n.translate(getAddLikeAlertKey('MESSAGE')),
        buttons: [
          { text: this.i18n.translate(getAddLikeAlertKey('NO')), cssClass: 'secondary' },
          { text: this.i18n.translate(getAddLikeAlertKey('YES')), role: 'confirm' },
        ],
      });

      await alert.present(); // Not a page - no need to tack pageview
      isNotificationEnabled = (await alert.onWillDismiss())?.role === 'confirm';
    }

    this.restaurantsService.setDishLike(this.restaurantId!, this.dish!.id, isLiked, isNotificationEnabled).subscribe();
  }

  async addDishToCart(dish: DishDto) {
    this.restaurantsService.addDishToCart(dish);
  }

  async removeDishFromCart(dish: DishDto) {
    this.restaurantsService.removeDishFromCart(dish.id);
  }

  async openCartModal() {
    await this.modalCtrl.dismiss({}, OPEN_CART_MODAL_ROLE);
  }
}

export const OPEN_CART_MODAL_ROLE = 'openCartModal';

const SINGLE_IMG_HEIGHT = 356;
const CARROUSEL_IMG_HEIGHT = 380;

const getAddLikeAlertKey = (key: string) => `DISH_DETAILS_COMPONENT.ADD_LIKE_ALERT .${key}`;
